<modal-dialog #inviteUsersModal [dialogTemplate]="inviteUsersDialogTemplate"></modal-dialog>
<ng-template #inviteUsersDialogTemplate>
  <div
    class="light-content-panel light-control-host margin-auto"
    [class.max-width-500]="!rbacEnabled"
    (keydown.escape)="close()"
    tabindex="0"
  >
    <div class="modal-header">
      <h2 class="panel-title margin-left">Invite Users</h2>
      <button type="button" class="modal-close-btn" (click)="close()">
        <i class="fa-utility fa-semibold fa-xmark"></i>
      </button>
    </div>
    <form [formGroup]="inviteForm" (ngSubmit)="onSendInvites()" (keydown.enter)="$event.preventDefault()">
      <div class="modal-body">
        <!-- Email List Section -->
        <div class="form-section">
          <div class="gap-8" [class.flex-row]="rbacEnabled">
            <div class="form-row flex-1">
              <div class="form-group">
                <label class="form-label">Email Addresses</label>
                <div class="email-input-with-button">
                  <div class="email-tags-input-container">
                    <div class="email-tags-wrapper">
                      <span
                        *ngFor="let email of emailTags; let i = index"
                        class="email-tag"
                        [class.invalid]="isEmailInvalid(email)"
                      >
                        {{ email }}
                        <i class="fa-solid fa-xmark tag-remove" (click)="removeEmailTag(i)"></i>
                      </span>
                      <input
                        type="text"
                        #emailInput
                        [value]="currentEmailInput"
                        (input)="onEmailInputChange($event)"
                        (keydown)="onEmailInputKeydown($event)"
                        (blur)="onEmailInputBlur()"
                        class="email-input"
                        placeholder="Type email addresses..."
                      />
                    </div>
                  </div>
                  <button
                    type="button"
                    class="add-email-button"
                    (click)="addEmailTagFromButton()"
                    [disabled]="!currentEmailInput.trim()"
                    title="Add email"
                  >
                    <i class="fa-utility fa-semibold fa-plus"></i>
                  </button>
                </div>
              </div>
              <div *ngIf="emailListErrors && emailInputTouched" class="input-error-text">
                {{ emailListErrors }}
              </div>
            </div>
            <div class="form-row flex-1">
              <!-- Identity Provider and External ID Section -->
              <div class="form-group">
                <label class="form-label">Identity Provider</label>
                <div class="select-wrapper full-width">
                  <select class="arpio-select full-width" formControlName="identityProvider">
                    <option *ngIf="ssoIdentityProviders.length > 1" value="">Select an identity provider</option>
                    <option *ngFor="let idp of ssoIdentityProviders" [value]="idp.identityProviderId">
                      {{ uiService.buildIdentityProviderName(idp) }}
                    </option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
        <hr *ngIf="rbacEnabled" />
        <!-- Create Assignments Section - Hidden when rbacEnabled is false -->
        <div
          *ngIf="rbacEnabled"
          class="flex-row full-width create-user-assignments-section"
          [ngStyle]="{ gap: accountService.rbacFullEnabled ? '2rem' : '0' }"
        >
          <!-- Initial Roles Table -->
          <div
            class="table-container"
            [ngStyle]="{ flex: accountService.rbacFullEnabled ? '1' : 'auto' }"
            style="width: 100%"
          >
            <div class="flex-row items-center gap-4 half-margin-bottom">
              <h4 class="whitespace-nowrap">Initial Roles</h4>
              <div class="filter-container">
                <input
                  *ngIf="isModalOpen()"
                  pInputText
                  type="text"
                  #rolesFilter
                  (input)="filterRoles($event)"
                  placeholder="Filter roles..."
                  class="arpio-input filter-input"
                />
              </div>
            </div>
            <p-table
              *ngIf="isModalOpen()"
              #rolesTable
              [value]="availableRoles"
              [globalFilterFields]="['name', 'accessLevel']"
              dataKey="id"
              [scrollable]="true"
              scrollHeight="300px"
              [tableStyle]="{ 'min-width': '20rem' }"
              [styleClass]="'p-datatable-sm'"
              [loading]="loadingRoles"
              [customSort]="true"
              (sortFunction)="customSort($event)"
            >
              <ng-template pTemplate="header">
                <tr>
                  <th class="checkbox-column">
                    <input
                      type="checkbox"
                      class="arpio-checkbox"
                      [checked]="areAllRolesSelected()"
                      [indeterminate]="areSomeRolesSelected()"
                      (change)="toggleAllRoles($event)"
                    />
                  </th>
                  <th pSortableColumn="name" class="role-name-column">
                    Role Name <p-sortIcon field="name"></p-sortIcon>
                  </th>
                  <th pSortableColumn="accessLevel">Access Level(s) <p-sortIcon field="accessLevel"></p-sortIcon></th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-role>
                <tr [ngClass]="{ 'default-role-row': isDefaultAccountRole(role) }">
                  <td class="checkbox-column">
                    <input
                      type="checkbox"
                      class="arpio-checkbox"
                      [checked]="isRoleSelected(role)"
                      (change)="toggleRole(role, $event)"
                    />
                  </td>
                  <td class="role-name-column">
                    <div>
                      {{ role.name }}
                    </div>
                  </td>
                  <td>
                    <div
                      class="access-level-badges"
                      *ngIf="role.accessLevels && role.accessLevels.length > 0; else singleAccessLevel"
                    >
                      <p-tag
                        *ngFor="let accessLevelInfo of role.accessLevels"
                        [severity]="
                          accessLevelInfo.accessLevel.includes('admin')
                            ? 'danger'
                            : accessLevelInfo.accessLevel.includes('read')
                              ? 'warning'
                              : 'info'
                        "
                        class="access-level-badge fit-width"
                        [pTooltip]="getAccessLevelTooltip(accessLevelInfo)"
                        tooltipStyleClass="custom-scrollable-tooltip badge-tooltip"
                        tooltipPosition="top"
                        [autoHide]="false"
                        [hideDelay]="200"
                        [escape]="false"
                        tooltipEvent="hover"
                        value="{{ accessLevelService.getAccessLevelDisplayName(accessLevelInfo.accessLevel) }}"
                      >
                      </p-tag>
                    </div>
                    <ng-template #singleAccessLevel>
                      <div>{{ accessLevelService.getAccessLevelDisplayName(role.accessLevel) }}</div>
                    </ng-template>
                  </td>
                </tr>
              </ng-template>
              <ng-template pTemplate="emptymessage">
                <tr>
                  <td colspan="3" class="empty-state-cell text-center">
                    <div class="empty-state-content">
                      <p class="empty-state-message">No roles available</p>
                      <p class="empty-state-submessage">Create roles in Role Management to assign them to users.</p>
                    </div>
                  </td>
                </tr>
              </ng-template>
              <ng-template pTemplate="summary">
                <div class="flex align-items-center justify-content-between">
                  <span
                    >Displaying {{ rolesTable.filteredValue?.length ?? rolesTable.value?.length }} of
                    {{ rolesTable.value?.length }} roles</span
                  >
                </div>
              </ng-template>
            </p-table>
          </div>

          <!-- Initial Groups Table -->
          <div *ngIf="accountService.rbacFullEnabled" class="table-container" style="flex: 1">
            <div class="flex-row items-center gap-4 half-margin-bottom">
              <h4 class="whitespace-nowrap">Initial Groups</h4>
              <div class="filter-container">
                <input
                  *ngIf="isModalOpen()"
                  pInputText
                  type="text"
                  #groupsFilter
                  (input)="filterGroups($event)"
                  placeholder="Filter groups..."
                  class="arpio-input filter-input"
                />
              </div>
            </div>
            <p-table
              *ngIf="isModalOpen()"
              #groupsTable
              [value]="availableGroups"
              [globalFilterFields]="['name', 'roleCount']"
              dataKey="id"
              [scrollable]="true"
              scrollHeight="300px"
              [tableStyle]="{ 'min-width': '20rem' }"
              [styleClass]="'p-datatable-sm'"
            >
              <ng-template pTemplate="header">
                <tr>
                  <th class="checkbox-column">
                    <input
                      type="checkbox"
                      class="arpio-checkbox"
                      [checked]="areAllGroupsSelected()"
                      [indeterminate]="areSomeGroupsSelected()"
                      (change)="toggleAllGroups($event)"
                    />
                  </th>
                  <th pSortableColumn="name">Group Name <p-sortIcon field="name"></p-sortIcon></th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-group>
                <tr>
                  <td class="checkbox-column">
                    <input
                      type="checkbox"
                      class="arpio-checkbox"
                      [checked]="isGroupSelected(group)"
                      (change)="toggleGroup(group, $event)"
                    />
                  </td>
                  <td>
                    <div>{{ group.name }}</div>
                  </td>
                </tr>
              </ng-template>
              <ng-template pTemplate="emptymessage">
                <tr>
                  <td colspan="2" class="empty-state-cell text-center">
                    <div class="empty-state-content">
                      <p class="empty-state-message">No groups available</p>
                      <p class="empty-state-submessage">Create groups in Group Management to assign them to users.</p>
                    </div>
                  </td>
                </tr>
              </ng-template>
              <ng-template pTemplate="summary">
                <div class="flex align-items-center justify-content-between">
                  <span
                    >Displaying {{ groupsTable.filteredValue?.length ?? groupsTable.value?.length }} of
                    {{ groupsTable.value?.length }} groups</span
                  >
                </div>
              </ng-template>
            </p-table>
          </div>
        </div>
      </div>

      <div class="modal-footer">
        <button type="button" class="arpio-secondary-button" (click)="close()">CANCEL</button>
        <button
          type="submit"
          class="arpio-button"
          [disabled]="!isFormValid || sendingInvites"
          data-cy="send-invites-button"
        >
          <i *ngIf="sendingInvites" class="fa-solid fa-spinner fa-spin"></i>
          {{ sendingInvites ? 'SENDING...' : 'SEND INVITES' }}
        </button>
      </div>
    </form>
  </div>
</ng-template>
